import type { SyncStatus } from '../store/state.ts';

interface Props {
  status: SyncStatus;
  pendingCount: number;
}

/**
 * État de la synchronisation.
 *
 * L'enjeu n'est pas décoratif : hors ligne, l'utilisateur doit savoir que son
 * travail est en attente **et** qu'il n'est pas perdu. Le nombre d'opérations en
 * attente est affiché pour cette raison — c'est la preuve visible que la file
 * existe et qu'elle est conservée.
 */
export default function SyncBadge({ status, pendingCount }: Props) {
  const pending = pendingCount > 0 ? `${pendingCount} modification${pendingCount > 1 ? 's' : ''} en attente` : null;

  if (status.phase === 'unauthorized') {
    return (
      <div className="badge badge--error" role="alert">
        {status.message}
      </div>
    );
  }

  if (status.phase === 'degraded') {
    return (
      <div className="badge badge--warn" role="status">
        {status.message}
        {pending && <span className="badge__detail">{pending}</span>}
      </div>
    );
  }

  if (status.phase === 'offline') {
    return (
      <div className="badge badge--warn" role="status">
        Hors ligne — tout est enregistré sur cet appareil.
        {pending && <span className="badge__detail">{pending}</span>}
      </div>
    );
  }

  // En marche normale, on n'affiche quelque chose que s'il reste du travail :
  // un bandeau « tout va bien » permanent serait du bruit.
  if (pending) {
    return (
      <div className="badge" role="status">
        Synchronisation…
        <span className="badge__detail">{pending}</span>
      </div>
    );
  }

  return null;
}
